<template>
  <div class="glass-card" :class="{ compact }" :style="{ opacity: isVisible ? 1 : 0, transform: `translateY(${isVisible ? 0 : 20}px)` }">

    <div class="chart-container">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { markRaw } from 'vue';
Chart.register(...registerables);

export default {
  name: 'CallActivity',
  props: {
    callData: {
      type: Array,
      default: () => [
        { date: 'Mon', calls: 0 },
        { date: 'Tue', calls: 0 },
        { date: 'Wed', calls: 0 },
        { date: 'Thu', calls: 0 },
        { date: 'Fri', calls: 0 },
        { date: 'Sat', calls: 0 },
        { date: 'Sun', calls: 0 },
      ]
    },
    chartType: {
      type: String,
      default: 'line',
      validator: (value) => ['line', 'bar'].includes(value)
    },
    valueKey: {
      type: String,
      default: 'calls'
    },
    label: {
      type: String,
      default: 'Calls'
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVisible: false,
      chart: null,
      revealTimer: null,
      isUnmounting: false
    };
  },
  mounted() {
    this.setupChart();

    // Trigger animation on mount with a slight delay
    setTimeout(() => {
      this.isVisible = true;
    }, 200);
  },
  watch: {
    callData: {
      deep: true,
      handler() {
        this.setupChart();
      }
    },
    valueKey() {
      this.setupChart();
    }
  },
  methods: {
    setupChart() {
      const canvas = this.$refs.chartCanvas;
      if (this.isUnmounting || !canvas?.isConnected) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Get data from callData
      const labels = this.callData.map(item => item.date);
      const data = this.callData.map(item => item[this.valueKey] || 0);

      // Create gradient for area fill
      const gradient = ctx.createLinearGradient(0, 0, 0, 250);
      gradient.addColorStop(0, 'rgba(14, 165, 233, 0.2)'); // Light blue with opacity
      gradient.addColorStop(1, 'rgba(14, 165, 233, 0.02)'); // Almost transparent

      // Configure dataset based on chart type
      const isLineChart = this.chartType === 'line';
      const dataset = {
        label: this.label,
        data: data,
        backgroundColor: isLineChart ? gradient : 'rgba(37, 98, 190)',
        borderColor: '#21c0eb', // Sky blue color
        borderWidth: isLineChart ? 2.5 : 0,
        tension: 0.35, // Slightly smoother curve for line charts
        fill: isLineChart,
        pointBackgroundColor: 'rgb(14, 165, 233)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: isLineChart ? 0 : undefined, // Hide points by default for line charts
        pointHoverRadius: isLineChart ? 6 : undefined,
        pointHitRadius: isLineChart ? 10 : undefined, // Larger hit area for tooltip activation
        borderRadius: isLineChart ? undefined : 4, // Rounded corners for bar charts
        borderSkipped: false
      };

      if (this.chart && this.chart.config.type === this.chartType) {
        this.chart.data.labels = labels;
        this.chart.data.datasets[0] = dataset;
        this.chart.options.scales.x.display = !this.compact;
        this.chart.options.scales.y.display = !this.compact;
        this.chart.options.scales.y.max = Math.max(...data) > 0
          ? Math.max(...data) * 1.2
          : 10;
        this.chart.options.scales.y.ticks.stepSize = Math.max(...data) > 0
          ? Math.ceil(Math.max(...data) / 5)
          : 2;
        this.chart.update('none');
        return;
      }

      this.destroyChart();
      if (this.isUnmounting || !canvas.isConnected) return;

      const existingChart = Chart.getChart(canvas);
      if (existingChart) existingChart.destroy();

      this.chart = markRaw(new Chart(ctx, {
        type: this.chartType,
        data: {
          labels: labels,
          datasets: [dataset]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            x: {
              display: !this.compact,
              grid: {
                display: false, // No vertical gridlines
                drawBorder: false
              },
              ticks: {
                color: 'rgb(100, 116, 139)', // Slate-500
                font: {
                  family: "'Inter', 'system-ui', sans-serif",
                  size: 12
                },
                padding: 10
              },
              border: {
                display: false
              }
            },
            y: {
              display: !this.compact,
              min: 0,
              max: Math.max(...data) > 0
                ? Math.max(...data) * 1.2
                : 10, // Default max value when all calls are zero
              grid: {
                color: 'rgba(226, 232, 240, 0.6)', // Very light gray
                lineWidth: 1,
                drawBorder: false
              },
              ticks: {
                color: 'rgb(100, 116, 139)', // Slate-500
                font: {
                  family: "'Inter', 'system-ui', sans-serif",
                  size: 12
                },
                padding: 10,
                stepSize: Math.max(...data) > 0
                  ? Math.ceil(Math.max(...data) / 5)
                  : 2, // Default step size when all calls are zero
              },
              border: {
                display: false
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'white',
              titleColor: 'rgb(15, 23, 42)', // Slate-900
              bodyColor: 'rgb(14, 165, 233)', // Sky-500
              titleFont: {
                size: 16,
                weight: 'bold',
                family: "'Inter', 'system-ui', sans-serif"
              },
              bodyFont: {
                size: 14,
                family: "'Inter', 'system-ui', sans-serif"
              },
              borderColor: 'rgba(226, 232, 240, 0.5)',
              borderWidth: 1,
              cornerRadius: 8,
              padding: 12,
              displayColors: false,
              caretSize: 0,
              caretPadding: 20,
              callbacks: {
                title: function (tooltipItems) {
                  return tooltipItems[0].label;
                },
                label: function (context) {
                  return `${context.dataset.label}: ${context.parsed.y}`;
                },
                labelTextColor: function () {
                  return 'rgb(14, 165, 233)'; // Sky-500 for the value text
                }
              }
            }
          },
          interaction: {
            mode: 'nearest',
            intersect: false,
            axis: 'x'
          },
          elements: {
            point: {
              // Show points only on hover for line charts, none for bar charts
              radius: function (context) {
                if (context.chart.config.type === 'bar') return 0;
                // For line charts, show point for Friday (index 4) or on hover
                return context.dataIndex === 4 ? 5 : 0;
              }
            }
          }
        }
      }));
    },
    destroyChart() {
      if (!this.chart) return;
      this.chart.stop();
      this.chart.destroy();
      this.chart = null;
    }
  },
  beforeUnmount() {
    this.isUnmounting = true;
    clearTimeout(this.revealTimer);
    this.destroyChart();
  }
};
</script>

<style scoped>
.chart-container {
  height: 200px;
  /* 64 * 4px = 256px */
  position: relative;
}

.glass-card {
  height: 100%;
}

.glass-card.compact .chart-container {
  height: 48px;
}
</style>